<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import BookIcon from '@/assets/icons/BookIcon.vue';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import CompetencesIcon from '@/assets/icons/CompetencesIcon.vue';
import DocumentPenIcon from '@/assets/icons/DocumentPenIcon.vue';
import SupportMeasureIcon from '@/assets/icons/SupportMeasureIcon.vue';
import { EQuickEntryKind } from '@/composables/quickEntry/quickEntry.enums';
import type { IRequiredActionPrompt } from '@/composables/requiredAction/requiredAction.types';
import {
  QuickEntryRequiredAction,
  type TQuickEntryRequiredAction,
} from '@/composables/quickEntry/quickEntry.validation';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import ComposerActionRow from '@/core/elements/ComposerActionRow.vue';
import ComposerChip from '@/core/elements/ComposerChip.vue';
import RequiredActionTarget from '@/core/elements/RequiredActionTarget.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import { getPerformanceAssessmentName } from '@/utils/performance.utils';
import { getSubjectTintStyle } from '@/utils/color.utils';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits<{
  'toggle:performance-assessment': [anchor: HTMLElement];
  'toggle:subject': [anchor: HTMLElement];
  'toggle:competences': [anchor: HTMLElement];
  'remove:subject': [];
  'clear:competences': [];
  'rate:individually': [];
  collapse: [];
  submit: [];
  'interact:requirement': [action: TQuickEntryRequiredAction];
}>();

const props = withDefaults(
  defineProps<{
    selectedKind: EQuickEntryKind;
    selectedPerformanceAssessment: Components.Schemas.PerformanceAssessmentDto | null;
    selectedSchoolSubject: Components.Schemas.SchoolSubjectDto | null;
    competenceCount: number;
    isSubmitting: boolean;
    disabled: boolean;
    performanceAssessmentPopoverOpen: boolean;
    subjectPopoverOpen: boolean;
    competencePopoverOpen: boolean;
    showIndividualRating: boolean;
    mobileExpanded?: boolean;
    performanceAssessmentReadOnly?: boolean;
    subjectReadOnly?: boolean;
    competencesReadOnly?: boolean;
    performanceAssessmentPrompt?: IRequiredActionPrompt | null;
    schoolSubjectPrompt?: IRequiredActionPrompt | null;
  }>(),
  {
    performanceAssessmentReadOnly: false,
    subjectReadOnly: false,
    competencesReadOnly: false,
    mobileExpanded: false,
    performanceAssessmentPrompt: null,
    schoolSubjectPrompt: null,
  },
);

const { t } = useI18n();

const typeLabel = computed(() => {
  if (props.selectedKind === EQuickEntryKind.Note) return t('quickEntry.typeNote');
  if (props.selectedKind === EQuickEntryKind.SupportMeasure) return t('quickEntry.typeSupportMeasure');
  return props.selectedPerformanceAssessment
    ? getPerformanceAssessmentName(props.selectedPerformanceAssessment, t)
    : t('quickEntry.selectPerformanceAssessment');
});

const competencesChipStyle = computed(() => getSubjectTintStyle(props.selectedSchoolSubject?.color));

const subjectChipRef = useTemplateRef('subjectChipRef');
const competencesChipRef = useTemplateRef('competencesChipRef');

const emitTogglePerformanceAssessment = (anchor: HTMLElement): void => {
  if (props.performanceAssessmentReadOnly) return;
  emit('toggle:performance-assessment', anchor);
};

const emitToggleSubject = (): void => {
  if (props.subjectReadOnly) return;
  if (subjectChipRef.value) emit('toggle:subject', subjectChipRef.value);
};
const emitToggleCompetences = (): void => {
  if (props.competencesReadOnly) return;
  if (competencesChipRef.value) emit('toggle:competences', competencesChipRef.value);
};

// anchor for opening the competence selection from a slash command
defineExpose({ competencesChip: competencesChipRef, subjectChip: subjectChipRef });
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-between gap-sm"
    :class="props.mobileExpanded && 'min-h-0 flex-col flex-nowrap items-stretch gap-md'"
  >
    <div class="flex flex-wrap items-center gap-sm">
      <RequiredActionTarget
        :prompt="props.performanceAssessmentPrompt"
        @dismiss="emit('interact:requirement', QuickEntryRequiredAction.PerformanceAssessment)"
      >
        <template #default="{ targetClass, describedBy }">
          <ComposerChip
            :class="targetClass"
            :aria-describedby="describedBy"
            appearance="neutral"
            :label="typeLabel"
            :disabled="props.disabled"
            :expanded="props.performanceAssessmentPopoverOpen"
            :read-only="props.performanceAssessmentReadOnly"
            :chevron="!props.performanceAssessmentReadOnly"
            @click="emitTogglePerformanceAssessment"
          >
            <template #icon>
              <DocumentPenIcon v-if="props.selectedKind === EQuickEntryKind.Note" class="size-3 shrink-0" />
              <SupportMeasureIcon
                v-else-if="props.selectedKind === EQuickEntryKind.SupportMeasure"
                class="size-3 shrink-0"
              />
              <AnyIcon
                v-else-if="props.selectedPerformanceAssessment"
                :icon-name="props.selectedPerformanceAssessment.symbol"
                class="size-3 shrink-0"
              />
            </template>
          </ComposerChip>
        </template>
      </RequiredActionTarget>

      <RequiredActionTarget
        :prompt="props.schoolSubjectPrompt"
        @dismiss="emit('interact:requirement', QuickEntryRequiredAction.SchoolSubject)"
      >
        <template #default="{ targetClass, describedBy }">
          <span v-if="props.selectedSchoolSubject" ref="subjectChipRef" class="relative inline-flex shrink-0">
            <SchoolSubjectBadge
              :school-subject="props.selectedSchoolSubject"
              :class="[
                '!h-chip !min-w-0 !gap-sm !overflow-hidden !px-smd !py-xs !text-footer-semibold !leading-footer !normal-case',
                targetClass,
              ]"
              :aria-describedby="describedBy"
            >
              <component
                :is="props.subjectReadOnly ? 'span' : 'button'"
                :type="props.subjectReadOnly ? undefined : 'button'"
                class="flex min-w-0 flex-1 items-center rounded-badge outline-inner disabled:opacity-40"
                :disabled="props.subjectReadOnly ? undefined : props.disabled"
                :aria-haspopup="props.subjectReadOnly ? undefined : 'dialog'"
                :aria-expanded="props.subjectReadOnly ? undefined : props.subjectPopoverOpen"
                :aria-describedby="describedBy"
                :aria-label="
                  props.subjectReadOnly
                    ? undefined
                    : `${$t('quickEntry.selectSubject')}: ${props.selectedSchoolSubject.name}`
                "
                @click="emitToggleSubject"
              >
                {{ props.selectedSchoolSubject.abbreviation }}
              </component>
              <span v-if="!props.subjectReadOnly" class="relative h-icon-xs w-icon-smd shrink-0">
                <button
                  type="button"
                  class="absolute -right-xxs top-xxs flex size-3 items-center justify-center rounded-button outline-inner disabled:opacity-40"
                  :disabled="props.disabled"
                  :aria-label="$t('quickEntry.removeSubject')"
                  @click="emit('remove:subject')"
                >
                  <CloseIcon class="size-3" />
                </button>
              </span>
            </SchoolSubjectBadge>
          </span>
          <span v-else ref="subjectChipRef" class="inline-flex shrink-0">
            <ComposerChip
              :class="targetClass"
              :aria-describedby="describedBy"
              appearance="ghost"
              :label="$t('quickEntry.commandSubject')"
              :disabled="props.disabled"
              :expanded="props.subjectPopoverOpen"
              @click="emit('toggle:subject', $event)"
            >
              <template #icon><BookIcon class="size-3 shrink-0" /></template>
            </ComposerChip>
          </span>
        </template>
      </RequiredActionTarget>

      <span ref="competencesChipRef" class="inline-flex shrink-0">
        <span
          v-if="props.competenceCount"
          :class="props.competencesReadOnly ? 'status-chip' : 'chip'"
          :style="competencesChipStyle"
        >
          <component
            :is="props.competencesReadOnly ? 'span' : 'button'"
            :type="props.competencesReadOnly ? undefined : 'button'"
            class="flex min-w-0 items-center gap-sm rounded-badge outline-inner"
            :disabled="props.competencesReadOnly ? undefined : props.disabled"
            :aria-haspopup="props.competencesReadOnly ? undefined : 'dialog'"
            :aria-expanded="props.competencesReadOnly ? undefined : props.competencePopoverOpen"
            @click="emitToggleCompetences"
          >
            <CompetencesIcon class="size-3 shrink-0" />
            <span>{{ $t('competence.count', { count: props.competenceCount }) }}</span>
          </component>
          <button
            v-if="!props.competencesReadOnly"
            type="button"
            class="-ml-xxs flex size-4 shrink-0 items-center justify-center rounded-button outline-inner"
            :disabled="props.disabled"
            :aria-label="$t('quickEntry.clearCompetences')"
            @click="emit('clear:competences')"
          >
            <CloseIcon class="size-3" />
          </button>
        </span>
        <ComposerChip
          v-else
          appearance="ghost"
          :label="$t('quickEntry.commandCompetences')"
          :disabled="props.disabled"
          :expanded="props.competencePopoverOpen"
          :accessible-label="$t('competence.select')"
          @click="emitToggleCompetences()"
        >
          <template #icon><CompetencesIcon class="size-3 shrink-0" /></template>
        </ComposerChip>
      </span>
    </div>

    <ComposerActionRow
      :disabled="props.disabled"
      :is-submitting="props.isSubmitting"
      :mobile-expanded="props.mobileExpanded"
      :submit-label="$t('quickEntry.saveEntry')"
      @collapse="emit('collapse')"
      @submit="emit('submit')"
    >
      <button
        v-if="props.showIndividualRating"
        type="button"
        class="whitespace-nowrap text-footer font-normal text-secondary underline outline-inner transition duration-300 hover:text-primary active:text-primary disabled:pointer-events-none disabled:opacity-40"
        :disabled="props.disabled || props.isSubmitting"
        @click="emit('rate:individually')"
      >
        {{ $t('quickEntry.rateIndividually') }}
      </button>
    </ComposerActionRow>
  </div>
</template>
